import React from "react";
import { QRCodeSVG } from "qrcode.react";

// SampleLabel — the printable jewellery hang tag for the CD410 label printer.
// Rendered identically in the Label Settings live preview and on PrintTagsPage so
// what the jeweller sees on screen is exactly what comes off the printer.
//
// Physical stock: 100 mm wide x 15 mm tall. Only the first 55 mm is printable;
// the tag is divided left-to-right into:
//   FRONT  0 – 27.5 mm   weights only, fixed order: G.Wt, N.Wt, St.Wt (if > 0),
//                        D.Wt (carats, if > 0), D.Quality (if present)
//   BACK   27.5 – 55 mm  shop name on top, then the QR (piece code) beside the
//                        identity lines: name+purity, SKU, HUID, selling price
//   TAIL   55 – 100 mm   blank — this strip loops through the ring/chain, so
//                        nothing is ever printed there
// All sizes are real CSS physical units (mm / pt) so the browser maps them 1:1 to
// the printer at 100%. There are deliberately NO borders, outlines or divider
// lines anywhere: thermal tags are die-cut, so any rule would print as a stray
// mark. The optional on-screen fold guides (`showGuides`) are dashed overlays
// for the settings preview only and are never rendered on the print page.
export const TAG_WIDTH_MM = 100;
export const TAG_HEIGHT_MM = 15;
export const FRONT_WIDTH_MM = 27.5;
export const BACK_WIDTH_MM = 27.5; // 27.5 -> 55; printable area ends at 55 mm
const PAD_MM = 0.8;

const fmtWeight = (v) => `${Number(v || 0).toFixed(3)}g`;

export function SampleLabel({ settings, demo, showGuides = false }) {
  const scale = Number(settings?.font_scale) || 1.0;

  // `??` (not `||`) on the text fields so a real piece can pass "" / null to print
  // nothing, rather than falling back to the sample placeholders — those only
  // appear in the Label Settings preview card where `demo` is undefined.
  const piece = {
    barcode: demo?.barcode || "MJ-SAMPLE-000042",
    product_sku: demo?.product_sku || "MJ-SAMPLE",
    product_name: demo?.product_name ?? "Ring",
    purity: demo?.purity || "22K",
    gross_weight_g: demo?.gross_weight_g ?? 3.25,
    net_weight_g: demo?.net_weight_g ?? 3.05,
    stone_weight_g: demo?.stone_weight_g ?? 0.2,
    // Carats: piece-level diamond_carat_total, or (resolved by PrintTagsPage) the
    // sum of the parent product's diamonds[].carat when the piece value is null.
    diamond_carat: demo?.diamond_carat ?? 0.5,
    // Comma-joined products.diamonds[].quality, e.g. "VVS-EF, VS-GH".
    diamond_quality: demo?.diamond_quality === undefined ? "VVS-EF" : demo.diamond_quality,
    price: demo?.price ?? 24999,
    huid: demo?.huid === undefined ? "ABC123" : demo.huid,
  };

  const shopName = (settings?.header_text ?? "MAAHEY").trim();
  const showStone = Number(piece.stone_weight_g) > 0;
  const showDiamondWt = Number(piece.diamond_carat) > 0;
  const showDiamondQuality = !!(piece.diamond_quality && String(piece.diamond_quality).trim());
  const showPrice = settings?.show_price && piece.price != null && piece.price !== "";

  // Back panel: shop name on top, then a row of QR (left) + identity lines (right).
  // The QR takes the full height left under the shop name, so an 18-character
  // piece code (25x25 modules) prints at ~0.4 mm/module — readable by 2D counter
  // scanners and phone cameras at tag distance.
  const headerPt = 4.5 * scale;
  const headerMm = headerPt * 0.3528 * 1.15; // pt -> mm, with line-height
  const qrSizeMm = Math.max(6, TAG_HEIGHT_MM - 2 * PAD_MM - headerMm - 0.3);

  // Front: 5 weight lines x 5.5pt x 1.15 line-height = ~11.2 mm inside 13.4 mm usable.
  const linePt = 5.5 * scale;
  // Identity lines sit beside the QR in ~15 mm of width, so they drop to 5pt.
  const identityPt = 5 * scale;

  return (
    <div
      className="bg-white text-black"
      style={{
        position: "relative",
        overflow: "hidden",
        width: `${TAG_WIDTH_MM}mm`,
        height: `${TAG_HEIGHT_MM}mm`,
        fontFamily: "'Source Code Pro', 'IBM Plex Mono', ui-monospace, monospace",
        boxSizing: "border-box",
        border: 0,
        outline: 0,
        boxShadow: "none",
      }}
      data-testid="sample-label-preview"
    >
      {/* ---- FRONT (0 – 27.5 mm): weights only, fixed order ---- */}
      <div
        className="flex flex-col"
        style={{
          position: "absolute", left: 0, top: 0,
          width: `${FRONT_WIDTH_MM}mm`, height: `${TAG_HEIGHT_MM}mm`,
          padding: `${PAD_MM}mm ${PAD_MM}mm ${PAD_MM}mm ${PAD_MM + 0.7}mm`,
          boxSizing: "border-box", fontSize: `${linePt}pt`, lineHeight: 1.15, textAlign: "left",
        }}
        data-testid="label-panel-front"
      >
        <div className="flex flex-col min-w-0" data-testid="label-back-weights">
          {settings?.show_gross && (
            <div className="whitespace-nowrap" data-testid="label-element-gross">G.Wt: {fmtWeight(piece.gross_weight_g)}</div>
          )}
          {settings?.show_net && (
            <div className="whitespace-nowrap" data-testid="label-element-net">N.Wt: {fmtWeight(piece.net_weight_g)}</div>
          )}
          {showStone && (
            <div className="whitespace-nowrap" data-testid="label-element-stone">St.Wt: {fmtWeight(piece.stone_weight_g)}</div>
          )}
          {showDiamondWt && (
            <div className="whitespace-nowrap" data-testid="label-element-diamond-wt">D.Wt: {Number(piece.diamond_carat).toFixed(2)}ct</div>
          )}
          {showDiamondQuality && (
            <div className="whitespace-nowrap truncate" data-testid="label-element-diamond-quality">D.Quality: {piece.diamond_quality}</div>
          )}
        </div>
      </div>

      {/* ---- BACK (27.5 – 55 mm): shop name, then QR + identity ---- */}
      <div
        className="flex flex-col"
        style={{
          position: "absolute", left: `${FRONT_WIDTH_MM}mm`, top: 0,
          width: `${BACK_WIDTH_MM}mm`, height: `${TAG_HEIGHT_MM}mm`,
          padding: `${PAD_MM}mm`, boxSizing: "border-box", textAlign: "left",
        }}
        data-testid="label-panel-back"
      >
        {shopName && (
          <div className="font-bold tracking-wider text-center whitespace-nowrap overflow-hidden w-full" style={{ fontSize: `${headerPt}pt`, lineHeight: 1.15 }} data-testid="label-element-shop-name">
            {shopName}
          </div>
        )}
        <div className="flex-1 flex items-start min-h-0" style={{ gap: "1mm", marginTop: "0.3mm" }}>
          <div className="shrink-0" data-testid="label-element-qr">
            <QRCodeSVG
              value={piece.barcode}
              level="M"
              marginSize={0}
              bgColor="#ffffff"
              fgColor="#000000"
              size={256}
              style={{ width: `${qrSizeMm}mm`, height: `${qrSizeMm}mm`, display: "block" }}
              aria-label={`QR code ${piece.barcode}`}
            />
          </div>
          <div className="flex flex-col min-w-0 flex-1" style={{ fontSize: `${identityPt}pt`, lineHeight: 1.15 }} data-testid="label-back-identity">
            {(piece.product_name || (settings?.show_purity && piece.purity)) && (
              <div className="flex items-baseline gap-[0.8mm] whitespace-nowrap min-w-0 font-semibold" data-testid="label-element-name-purity">
                {piece.product_name && <span className="truncate min-w-0">{piece.product_name}</span>}
                {settings?.show_purity && piece.purity && <span className="shrink-0" data-testid="label-element-purity">{piece.purity}</span>}
              </div>
            )}
            {settings?.show_sku && piece.product_sku && (
              <div className="whitespace-nowrap truncate" data-testid="label-element-sku">{piece.product_sku}</div>
            )}
            {settings?.show_huid && piece.huid && (
              <div className="whitespace-nowrap truncate" data-testid="label-element-huid">HUID: {piece.huid}</div>
            )}
            {showPrice && (
              <div className="whitespace-nowrap font-semibold" data-testid="label-element-price">
                {"\u20B9"}{Number(piece.price || 0).toLocaleString("en-IN")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- TAIL (55 – 100 mm): intentionally empty ---- */}

      {/* On-screen fold guides for the settings preview only (never printed). */}
      {showGuides && (
        <>
          <div className="print:hidden pointer-events-none" style={{ position: "absolute", top: 0, bottom: 0, left: `${FRONT_WIDTH_MM}mm`, borderLeft: "1px dashed rgba(120,120,120,0.45)" }} aria-hidden="true" />
          <div className="print:hidden pointer-events-none" style={{ position: "absolute", top: 0, bottom: 0, left: `${FRONT_WIDTH_MM + BACK_WIDTH_MM}mm`, borderLeft: "1px dashed rgba(120,120,120,0.45)" }} aria-hidden="true" />
          <div className="print:hidden pointer-events-none text-[6px] uppercase tracking-widest text-neutral-400" style={{ position: "absolute", left: `${FRONT_WIDTH_MM + BACK_WIDTH_MM}mm`, right: 0, top: "40%", textAlign: "center" }} aria-hidden="true">blank tail</div>
        </>
      )}
    </div>
  );
}
